import React from "react";
//#region redux
import { displayDialog } from "store/actions/utilActions";
import { ThunkDispatch } from "redux-thunk";
import { useDispatch } from "react-redux";
//#endregion
//#region types
import { FormFieldProps, TableListSelectionFieldProps } from "models/propTypes/genericFormProps";
import { DialogEnums } from "models/enums";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import { FieldArray, FieldArrayRenderProps } from "formik";
import useTableListFormFieldHelpers from "../baseFields/useTableListFormFieldHelpers";
import TableList from "../baseFields/tableList/tableList";
import ReadOnlyField from "../baseFields/readOnlyField";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

function TableListSelectionFormField<T extends Record<string, any>>(props: FormFieldProps<T>) {
  const { name, field, value, readonly } = props;
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();

  const extraProps = field.extraProps as TableListSelectionFieldProps<T>;

  const entityText = LocalizationHelperInstance.getFormattedMessage(extraProps.entityText).toLowerCase();

  const { getProperties } = useTableListFormFieldHelpers<T>(
    value,
    extraProps.resultTableProperties,
    extraProps.idFieldKey,
    extraProps.moreActions,
    extraProps.defaultRefField,
    extraProps.hideMoreActionsInForm,
    readonly
  );

  const onAddNewClick = (arrayHelpers: FieldArrayRenderProps) => {
    dispatch(
      displayDialog({
        isOpen: false,
        title: `${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.AddEntity)} ${entityText}`,
        titleIcon: extraProps.icon,
        subTitle: extraProps?.addItemDialogSubTitle,
        dialogType: DialogEnums.DialogType.Selection,
        children: {
          getSuggestions: extraProps.getSuggestions,
          search: extraProps?.search,
          multiSelection: true,
          excludeIds: value?.map((v: any) => (extraProps.idFieldKey ? v[extraProps.idFieldKey] : v.id)),
        },
        onSubmit: (values: any[]) => {
          if (values) {
            values.forEach((v) => arrayHelpers.push(extraProps?.valueFormatter ? extraProps.valueFormatter(v) : value));
          }
        },
        maxWidth: "sm",
      })
    );
  };

  const component = (
    <FieldArray
      name={name}
      render={(arrayHelpers) => (
        <TableList
          entityText={entityText}
          properties={getProperties(arrayHelpers)}
          value={value}
          onItemClick={() => {}}
          onAddNewClick={onAddNewClick.bind(null, arrayHelpers)}
          isReadOnly={readonly}
          orderByProperty={extraProps.orderByProperty}
          disableItemClick
          disableSort={extraProps.disableSort}
          activeKey={extraProps.activeKey}
          hideHeader={extraProps.hideHeader}
          hideAddNewButton={extraProps.hideAddNewButton}
        />
      )}
    />
  );

  return readonly ? (
    <ReadOnlyField label={field.label} href={field.href}>
      {component}
    </ReadOnlyField>
  ) : (
    component
  );
}

export default TableListSelectionFormField;
